import AccessCredentialHelp from './AccessCredentialHelp';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { AddCircle, Groups } from '@mui/icons-material';
import { Chip, IconButton, Stack, Typography } from '@mui/material';

import { useApiClients } from '@@common';

import { ClientModel } from '../../../api/api';
import SidePanelList, { SidePanelListColumn } from '../../../components/Panels/SidePanelList';
import CreateClientDialog from './CreateClientDialog';

interface ClientsPanelProps {
	onClose: () => void;
	onClientSelect: (clientId: string) => void;
	selectedClientId?: string | null;
	onCollapseChange?: (collapsed: boolean) => void;
	width: number;
	setWidth: Dispatch<SetStateAction<number>>;
	top?: number;
}

export const CLIENTS_PANEL_STORAGE_KEY = 'clientsPanel.width';
export const CLIENTS_PANEL_DEFAULT_WIDTH = 500;
export const CLIENTS_PANEL_MIN_WIDTH = 360;
export const CLIENTS_PANEL_MAX_WIDTH = 900;
export const CLIENTS_PANEL_COLLAPSED_WIDTH = 48;

export default function ClientsPanel({ onClose, onClientSelect, selectedClientId, onCollapseChange, width, setWidth, top = 0 }: ClientsPanelProps) {
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [openCreateDialog, setOpenCreateDialog] = useState(false);
	const { data, isLoading, isFetching, error, refetch } = useApiClients();

	const clients = useMemo(() => data || [], [data]);
	const columns: SidePanelListColumn[] = [
		{ id: 'name', label: 'Name', align: 'left' },
		{ id: 'code', label: 'Code', align: 'left' },
		{ id: 'dependencies', label: 'Deps', align: 'center', width: 72 },
	];

	const handleToggleCollapse = () => {
		const next = !isCollapsed;
		setIsCollapsed(next);
		onCollapseChange?.(next);
	};

	const renderCell = (item: ClientModel, columnId: string) => {
		switch (columnId) {
			case 'name':
				return (
					<Stack direction="row" spacing={1} alignItems="center">
						<Groups sx={{ fontSize: 16, color: 'primary.main' }} />
						<Typography variant="body2" sx={{ fontWeight: 500 }}>
							{item.name}
						</Typography>
					</Stack>
				);
			case 'code':
				return (
					<Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
						{item.code}
					</Typography>
				);
			case 'dependencies':
				return <Chip label={item.dependencies?.length || 0} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
			default:
				return null;
		}
	};

	return (
		<>
			<SidePanelList
				footer={<AccessCredentialHelp kind="client" />}
				title="API Clients"
				subtitle="Logical external API clients"
				items={clients}
				columns={columns}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error as Error | null}
				emptyMessage="No API clients found"
				selectedItemId={selectedClientId}
				width={width}
				collapsedWidth={CLIENTS_PANEL_COLLAPSED_WIDTH}
				isCollapsed={isCollapsed}
				onClose={onClose}
				onRefresh={() => refetch()}
				onItemSelect={(item) => onClientSelect(item.id)}
				renderCell={renderCell}
				getItemId={(item) => item.id}
				onToggleCollapse={handleToggleCollapse}
				resizeStorageKey={CLIENTS_PANEL_STORAGE_KEY}
				defaultWidth={CLIENTS_PANEL_DEFAULT_WIDTH}
				minWidth={CLIENTS_PANEL_MIN_WIDTH}
				maxWidth={CLIENTS_PANEL_MAX_WIDTH}
				setWidth={setWidth}
				top={top}
				headerActions={
					<IconButton size="small" onClick={() => setOpenCreateDialog(true)} title="Create client">
						<AddCircle />
					</IconButton>
				}
			/>
			<CreateClientDialog
				open={openCreateDialog}
				onClose={() => setOpenCreateDialog(false)}
				onCreated={(clientId) => {
					setOpenCreateDialog(false);
					refetch();
					onClientSelect(clientId);
				}}
			/>
		</>
	);
}
